import { Outlet, NavLink } from 'react-router-dom'
import { ArrowLeft, FlaskConical, GitBranch, FileText, Activity, Wrench, Files, Database, Play, Settings as SettingsIcon, LogOut, Sparkles } from 'lucide-react'
import { useAuth } from '../auth'
import ActAsPicker from './ActAsPicker'
import TweaksPanel from './TweaksPanel'

const studioTabs = [
  { to: '/studio',                label: 'Overview',      icon: FlaskConical, end: true },
  { to: '/studio/artifacts',      label: 'Artifacts',     icon: Files },
  { to: '/studio/pipeline',       label: 'Pipeline',      icon: GitBranch },
  { to: '/studio/pipeline-test',  label: 'Test run',      icon: Play },
  { to: '/studio/prompts',        label: 'Prompts',       icon: FileText },
  { to: '/studio/test-sets',      label: 'Test sets',     icon: Wrench },
  { to: '/studio/observability',  label: 'Observability', icon: Activity },
  { to: '/studio/imports',        label: 'Imports',       icon: Database },
  { to: '/studio/ai-config',      label: 'AI config',     icon: Sparkles },
]

export default function StudioLayout() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null

  return (
    <div className="flex h-screen flex-col bg-[color:var(--me-bg)]">
      <header className="flex items-center gap-1 border-b me-border bg-[color:var(--me-surface)] px-3 py-2">
        <NavLink to="/workspace" className="me-tab" title="Back to workspace">
          <ArrowLeft size={14} />
        </NavLink>
        <div className="me-mono mr-3 ml-2 flex items-center gap-2 text-[13px] font-semibold tracking-tight">
          <span className="inline-block h-3 w-3 rounded-sm bg-[color:var(--me-fg)]" />
          Matters
          <span className="me-text-mute me-mono">/ studio</span>
        </div>
        <nav className="flex flex-1 gap-0.5">
          {studioTabs.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end ?? false}>
              {({ isActive }) => (
                <span data-selected={isActive ? 'true' : undefined} className="me-tab inline-flex items-center gap-1.5">
                  <Icon size={14} strokeWidth={1.6} />
                  {label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        {/* Same right-side cluster as the workspace header — keeps user
         *  affordances (act-as, settings, theme tweaks, logout) accessible
         *  from any Studio screen without bouncing back to /workspace. */}
        <div className="flex items-center gap-2 text-[12px] me-text-mute">
          {user && (
            <ActAsPicker user={user} onChange={() => auth.refresh()} />
          )}
          {user && (
            <span
              className="me-mono"
              title={user.acting_as
                ? `Logged in as ${user.acting_as.real_username} (superuser); acting as ${user.username}`
                : user.username}
            >
              {user.username}
              {user.client && (
                <span className="me-text-mute"> · {user.client.code}</span>
              )}
            </span>
          )}
          <NavLink
            to="/settings"
            className="me-btn me-btn-outline"
            title="Settings"
          >
            <SettingsIcon size={13} />
          </NavLink>
          <TweaksPanel />
          <button onClick={() => auth.logout()} className="me-btn me-btn-outline" title="Log out">
            <LogOut size={13} />
          </button>
        </div>
      </header>
      <main className="me-scroll flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
